import { DotLegalButton, DotLegalCheckbox } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { isNullOrWhitespace } from "../../common/stringOperations";
import { useTranslation } from "../../localization/useTranslation";
import { useCreateCommentStyle } from "./CreateComment.styles";
import { CommentType } from "../commentDialog/CommentDialog.types";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useUrlProvider } from "../../useUrlProvider";
import { Link } from "react-router";
import { useStateSessionStorage } from "../../common/hooks/useStateSessionStorage";
import MentionInput, { MentionInputRef } from "../../common/components/mentionInput/MentionInput.tsx";
import { EntityType } from "../../common/Common.types.ts";
import { RelationSaveModel } from "../../taskManagement/createTaskPane/CreateTaskPane.types.ts";

export interface ICreateEditComment {
    onCommentSubmitted: (content: string, mentions: Array<RelationSaveModel>) => void;
    onSubscriptionChanged: (value: boolean) => void;
    onCancelClicked: () => void;
    id: string;
    commentType: CommentType;
    content?: string;
    subscribed?: boolean;
    isSaving: boolean | undefined;
    notificationText: string;
    expandInputOnFocus?: boolean;
    disabled?: boolean;
    disableInitialTextFieldFocus?: boolean;
    textFieldOnTop?: boolean;
    disabledText?: string;
    shouldResetSessionContent: boolean;
    entityType: EntityType;
    maxRows?: number;
}

function CreateEditComment(props: ICreateEditComment) {
    const [saveModel, setSaveModel] = useStateSessionStorage<string | undefined>(props.id, props.content);
    const { helpLink } = useUserContext();
    const { getHelpUrl } = useUrlProvider();
    const mentionInputRef = useRef<MentionInputRef>(null);

    useEffect(() => {
        if (props.content) {
            setSaveModel(props.content);
            if (mentionInputRef.current) {
                mentionInputRef.current.editor.commands.focus();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.content]);

    // Runs on mount - Handle when the components mouunts. First time or refresh
    useEffect(() => {
        if (props.shouldResetSessionContent) {
            setSaveModel(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.shouldResetSessionContent]);

    useEffect(() => {
        if (mentionInputRef.current && (!props.disableInitialTextFieldFocus || props.commentType !== CommentType.CreateComment)) {
            mentionInputRef.current.editor.commands.focus();
        }
    }, [props.commentType, props.disableInitialTextFieldFocus]);

    const { translateString } = useTranslation();
    const styles = useCreateCommentStyle();

    const isEdit = props.commentType === CommentType.EditComment || props.commentType === CommentType.EditSubComment;

    return (
        <Box sx={{ flexShrink: 0 }}>
            <MentionInput
                ref={mentionInputRef}
                value={props.disabled && props.disabled ? props.disabledText! : (saveModel ?? "")}
                onChange={(content) => {
                    setSaveModel(content);
                }}
                mentionSource={{ entityFromId: props.id, entityFromType: EntityType.Task }}
                label={
                    isEdit
                        ? translateString("editComment")
                        : props.commentType === CommentType.CreateComment
                          ? translateString("addComment")
                          : translateString("replyComment")
                }
                disabled={props.isSaving || props.disabled}
                multiline={!props.expandInputOnFocus}
                rows={4}
                expandToMultilineOnFocus={props.expandInputOnFocus}
                initialTextFieldFocus={!props.disableInitialTextFieldFocus}
                manageRelationsExternal
                popperPlacement={props.entityType === EntityType.ProcessingActivity ? "top-end" : undefined}
                reportEveryChange
                maxRows={props.maxRows}
            />

            {props.disabled && props.entityType === EntityType.ProcessingActivity && (
                <Box
                    component={Link}
                    sx={(theme) => ({
                        fontSize: 12,
                        color: theme.palette.primary.main,
                        textDecoration: "none",
                        "&:hover": {
                            textDecoration: "underline",
                        },
                    })}
                    to={getHelpUrl(helpLink)}
                    target={"_blank"}
                >
                    {translateString("getMoreInfoHere")}
                </Box>
            )}

            <Box sx={styles.btnContainer}>
                <React.Fragment>
                    {props.commentType === CommentType.CreateComment ? (
                        <Box sx={{ marginLeft: props.textFieldOnTop ? "-4px" : "-12px" }}>
                            <DotLegalCheckbox
                                label={props.notificationText}
                                onChange={async (subscribe: boolean) => props.onSubscriptionChanged(subscribe)}
                                checked={props.subscribed ?? false}
                                fontSize="s"
                                disabled={props.disabled}
                            />
                        </Box>
                    ) : (
                        <DotLegalButton
                            onClick={async () => {
                                setSaveModel(undefined);
                                props.onCancelClicked();
                            }}
                            buttonType={"danger"}
                        >
                            {translateString("cancel")}
                        </DotLegalButton>
                    )}

                    <DotLegalButton
                        disabled={props.isSaving || isNullOrWhitespace(saveModel) || props.disabled}
                        onClick={async () => {
                            setSaveModel(undefined);

                            const mentions: Array<RelationSaveModel> = [];
                            if (mentionInputRef.current) {
                                const mentionTypes = ["mention@", "mention/", "mention#"];
                                mentionInputRef.current.editor.state.doc.descendants((node: any) => {
                                    if (mentionTypes.includes(node.type.name)) {
                                        mentions.push({
                                            fromEntityId: props.id,
                                            fromEntityType: props.entityType,
                                            toEntityId: node.attrs.id, // The RelationsSaveModel has nullable toId.. When Create Declaration, no fromId exists
                                            toEntityType: Number(node.attrs.entityType),
                                        });
                                    }
                                });
                            }

                            props.onCommentSubmitted(saveModel!, mentions);
                        }}
                        buttonType={"primary"}
                    >
                        {isEdit ? translateString("save") : translateString("send")}
                    </DotLegalButton>
                </React.Fragment>
            </Box>
        </Box>
    );
}

export default CreateEditComment;
